/**
 * Health Data — READ-ONLY mirror of HealthKit / Health Connect.
 *
 * A PWA cannot read Apple HealthKit or Android Health Connect (no web API).
 * The native apps (Swift / Kotlin) read those platforms and push
 * snapshots to D1; this page only DISPLAYS them. Shows the latest snapshot per
 * source (GET /api/health/all), so a user with two devices sees both.
 * Mirrors the Swift HealthView sections. Luxury design system.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Heart, Activity, Moon, User, Footprints, Flame } from 'lucide-react';
import { api } from '@/lib/api';
import { HealthSyncNotice } from '@/components/HealthSyncNotice';

interface Snapshot {
  source: string;
  recorded_at: string;
  steps: number | null;
  steps_goal: number | null;
  active_calories: number | null;
  exercise_minutes: number | null;
  stand_minutes: number | null;
  distance_miles: number | null;
  flights_climbed: number | null;
  heart_rate_avg: number | null;
  heart_rate_resting: number | null;
  heart_rate_max: number | null;
  hrv: number | null;
  vo2_max: number | null;
  weight: number | null;
  body_fat_pct: number | null;
  bmi: number | null;
  sleep_hours: number | null;
  sleep_goal: number | null;
  sleep_deep_hours: number | null;
  sleep_rem_hours: number | null;
}

const SOURCE_LABEL: Record<string, string> = {
  apple_health: 'Apple Health',
  health_connect: 'Health Connect',
  manual: 'Manual',
};

function parseTs(iso: string): number {
  return new Date(iso.includes('T') || iso.includes('Z') ? iso : iso.replace(' ', 'T') + 'Z').getTime();
}

function ago(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - parseTs(iso)) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const STALE_HOURS = 12;
function isStale(iso: string): boolean {
  return Date.now() - parseTs(iso) > STALE_HOURS * 3600_000;
}

function Tile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
      <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--cream-muted)' }}>{label}</p>
      <p className="mt-1" style={{ fontFamily: 'var(--font-display), Georgia, serif', fontSize: '1.5rem', color: color || 'var(--cream)', lineHeight: 1 }}>{value}</p>
      {sub && <p className="text-[11px] mt-0.5" style={{ color: 'var(--cream-muted)' }}>{sub}</p>}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>{title}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

const has = (...v: (number | null)[]) => v.some((x) => x != null);
const n = (x: number | null, d = 0) => (x == null ? '--' : x.toLocaleString(undefined, { maximumFractionDigits: d }));

function SnapshotView({ s }: { s: Snapshot }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between px-1">
        <span className="eyebrow">{SOURCE_LABEL[s.source] ?? s.source}</span>
        <span className="text-xs" style={{ color: isStale(s.recorded_at) ? 'var(--warning)' : 'var(--cream-muted)' }}>
          Synced {ago(s.recorded_at)}
        </span>
      </div>
      {isStale(s.recorded_at) && (
        <p className="text-xs px-1 -mt-2" style={{ color: 'var(--cream-muted)' }}>
          This may be out of date. Open the app and tap <strong style={{ color: 'var(--cream-dim)' }}>Sync Now</strong> to refresh.
        </p>
      )}

      {has(s.steps, s.active_calories, s.exercise_minutes, s.distance_miles, s.flights_climbed) && (
        <Section title="Activity" icon={<Activity size={16} style={{ color: 'var(--success)' }} />}>
          {s.steps != null && <Tile label="Steps" value={n(s.steps)} sub={s.steps_goal ? `goal ${n(s.steps_goal)}` : undefined} color="var(--success)" />}
          {s.active_calories != null && <Tile label="Active Cal" value={n(s.active_calories)} />}
          {s.exercise_minutes != null && <Tile label="Exercise" value={`${n(s.exercise_minutes)} min`} />}
          {s.distance_miles != null && <Tile label="Distance" value={`${n(s.distance_miles, 1)} mi`} />}
          {s.flights_climbed != null && <Tile label="Flights" value={n(s.flights_climbed)} />}
          {s.stand_minutes != null && <Tile label="Stand" value={`${n(s.stand_minutes)} min`} />}
        </Section>
      )}

      {has(s.heart_rate_avg, s.heart_rate_resting, s.heart_rate_max, s.hrv, s.vo2_max) && (
        <Section title="Heart" icon={<Heart size={16} style={{ color: 'var(--error)' }} />}>
          {s.heart_rate_avg != null && <Tile label="Avg HR" value={`${n(s.heart_rate_avg)} bpm`} color="var(--error)" />}
          {s.heart_rate_resting != null && <Tile label="Resting" value={`${n(s.heart_rate_resting)} bpm`} />}
          {s.heart_rate_max != null && <Tile label="Max" value={`${n(s.heart_rate_max)} bpm`} />}
          {s.hrv != null && <Tile label="HRV" value={`${n(s.hrv)} ms`} />}
          {s.vo2_max != null && <Tile label="VO2 Max" value={n(s.vo2_max, 1)} />}
        </Section>
      )}

      {has(s.weight, s.body_fat_pct, s.bmi) && (
        <Section title="Body" icon={<User size={16} style={{ color: 'var(--champagne)' }} />}>
          {s.weight != null && <Tile label="Weight" value={`${n(s.weight, 1)} lbs`} />}
          {s.body_fat_pct != null && <Tile label="Body Fat" value={`${n(s.body_fat_pct, 1)}%`} />}
          {s.bmi != null && <Tile label="BMI" value={n(s.bmi, 1)} />}
        </Section>
      )}

      {has(s.sleep_hours, s.sleep_deep_hours, s.sleep_rem_hours) && (
        <Section title="Sleep" icon={<Moon size={16} style={{ color: 'var(--info)' }} />}>
          {s.sleep_hours != null && <Tile label="Sleep" value={`${n(s.sleep_hours, 1)} hrs`} sub={s.sleep_goal ? `goal ${n(s.sleep_goal, 0)}h` : undefined} color="var(--info)" />}
          {s.sleep_deep_hours != null && <Tile label="Deep" value={`${n(s.sleep_deep_hours, 1)} hrs`} />}
          {s.sleep_rem_hours != null && <Tile label="REM" value={`${n(s.sleep_rem_hours, 1)} hrs`} />}
        </Section>
      )}
    </div>
  );
}

export default function HealthData() {
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ snapshots: Snapshot[] }>('/api/health/all')
      .then((d) => setSnapshots(d.snapshots || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--champagne)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: '1.9rem' }}>Health Data</h1>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2].map((i) => <div key={i} className="card animate-pulse h-32" style={{ background: 'var(--surface)' }} />)}
        </div>
      ) : snapshots.length === 0 ? (
        <div className="flex flex-col gap-4">
          <div className="card text-center py-8">
            <div className="flex justify-center gap-3 mb-4" style={{ color: 'var(--champagne)' }}>
              <Footprints size={26} /><Heart size={26} /><Flame size={26} />
            </div>
            <p className="font-semibold" style={{ color: 'var(--cream)' }}>No health data yet</p>
          </div>
          <HealthSyncNotice />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {snapshots.map((s) => <SnapshotView key={s.source} s={s} />)}
        </div>
      )}
    </div>
  );
}
